import { useState, useCallback, useEffect, useMemo } from 'react'
import { DateRange } from '@mui/x-date-pickers-pro'
import { eachDayOfInterval, format, parseISO } from 'date-fns'
import { useAppDispatch } from '../../../redux/store'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import { manualRequestsService } from '../../../services/myActionsService'
import { isValidPlannerDate } from '../../../utils/date-utils'
import { LedgerBreakdownRow, LedgerOverviewRow, AbsenceLedgerMode } from '../../../types/osp-ledger'
import { AbsenceOccurrence, AbsenceDayRequest } from '../../../types/absence'
import { EmployeeOSPBalance, EmployeeBalanceTransaction } from '../../../types/osp-transactions'

const CSP100_CODE = 'CSP100'
const CSP50_CODE = 'CSP50'
const DATE_DISPLAY_FORMAT = 'd MMM yyyy'

export interface UseAbsenceLedgerParams {
  employeeId?: number
  dateRange: DateRange<Date> | [Date | null, Date | null]
  enabled: boolean
  extendAbsenceData?: AbsenceOccurrence
  selectedDays?: AbsenceDayRequest[] | null
  discretionaryPayHours?: number | null
  mode?: AbsenceLedgerMode
}

export interface UseAbsenceLedgerReturn {
  overviewRows: LedgerOverviewRow[]
  breakdownRows: LedgerBreakdownRow[]
  totalUnpaidBeforeDP: number
  unpaidPerDayBeforeDP: Record<string, number>
  isLoading: boolean
}

interface ChronoEvent {
  date: Date
  kind: 'absence' | 'recredit'
  hours: number
  isOriginal: boolean
  isUnchecked: boolean
  hundredCredit: number
  fiftyCredit: number
}

const formatPendingDP = (value: number): string =>
  value > 0 ? `${value.toFixed(2)} (pending)` : '0.00'

const findBalanceHours = (balances: EmployeeOSPBalance[], code: string): number =>
  balances.find(balance => balance.balanceXRefCode === code)?.hours ?? 0

export const rewindToPeriodStart = (
  currentHours: number,
  transactions: EmployeeBalanceTransaction[],
  code: string,
  fromDate: Date | null,
  toDate: Date | null = null
): number => {
  if (!isValidPlannerDate(fromDate)) return Math.max(0, currentHours)
  const fromDateStart = new Date(fromDate)
  fromDateStart.setHours(0, 0, 0, 0)
  const toDateEnd = isValidPlannerDate(toDate) ? new Date(toDate as Date) : null
  toDateEnd?.setHours(23, 59, 59, 999)
  const inPeriodSum = transactions
    .filter(transaction => transaction.balanceXRefCode === code)
    .filter(transaction => {
      const txDate = new Date(transaction.transactionDate)
      if (txDate < fromDateStart) return false
      // Dayforce applies transactions to currentBalance on their scheduled date,
      // not at save time. Future-dated transactions (both negatives and positives)
      // exist in the API response but haven't yet hit the pot, so exclude them
      // when reconstructing the starting balance.
      if (toDateEnd && txDate > toDateEnd) return false
      return true
    })
    .reduce((sum, transaction) => sum + transaction.amount, 0)
  return Math.max(0, currentHours - inPeriodSum)
}

export const projectForwardToPeriodStart = (
  currentHours: number,
  transactions: EmployeeBalanceTransaction[],
  code: string,
  fromDate: Date | null,
  toDate: Date | null
): number => {
  if (!isValidPlannerDate(fromDate) || !isValidPlannerDate(toDate)) return Math.max(0, currentHours)
  const fromDateStart = new Date(fromDate)
  fromDateStart.setHours(0, 0, 0, 0)
  const toDateStart = new Date(toDate)
  toDateStart.setHours(0, 0, 0, 0)
  if (toDateStart <= fromDateStart) return Math.max(0, currentHours)
  const interimSum = transactions
    .filter(transaction => transaction.balanceXRefCode === code)
    .filter(transaction => {
      const txDate = new Date(transaction.transactionDate)
      return txDate >= fromDateStart && txDate < toDateStart
    })
    .reduce((sum, transaction) => sum + transaction.amount, 0)
  return Math.max(0, currentHours + interimSum)
}

const buildAbsenceEvent = (
  date: Date,
  hours: number,
  options: { isOriginal?: boolean; isUnchecked?: boolean } = {}
): ChronoEvent => ({
  date,
  kind: 'absence',
  hours,
  isOriginal: options.isOriginal ?? false,
  isUnchecked: options.isUnchecked ?? false,
  hundredCredit: 0,
  fiftyCredit: 0,
})

const buildRecreditEvent = (
  date: Date,
  hundredCredit: number,
  fiftyCredit: number
): ChronoEvent => ({
  date,
  kind: 'recredit',
  hours: 0,
  isOriginal: false,
  isUnchecked: false,
  hundredCredit,
  fiftyCredit,
})

const buildAbsencePeriodLabel = (start: Date | null, end: Date | null): string => {
  if (!isValidPlannerDate(start) || !isValidPlannerDate(end)) return ''
  return `${format(start, DATE_DISPLAY_FORMAT)} - ${format(end, DATE_DISPLAY_FORMAT)}`
}

export const useAbsenceLedger = ({
  employeeId,
  dateRange,
  enabled,
  extendAbsenceData,
  selectedDays,
  discretionaryPayHours,
  mode = AbsenceLedgerMode.Simulation,
}: UseAbsenceLedgerParams): UseAbsenceLedgerReturn => {
  const dispatch = useAppDispatch()

  const [currentBalances, setCurrentBalances] = useState<EmployeeOSPBalance[]>([])
  const [employeeTransactions, setEmployeeTransactions] = useState<EmployeeBalanceTransaction[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const startDate = useMemo(
    () =>
      extendAbsenceData?.startDate
        ? parseISO(extendAbsenceData.startDate)
        : dateRange[0],
    [extendAbsenceData?.startDate, dateRange[0]]
  )
  const endDate = dateRange[1]

  const fetchLedger = useCallback(async () => {
    if (!employeeId || !isValidPlannerDate(startDate) || !isValidPlannerDate(endDate)) return

    setIsLoading(true)
    try {
      // Step 1 — Fetch the data: starting OSP balances + every transaction in the period
      const response = await manualRequestsService.getEmployeeAbsenceTransactionsAndOSPBalance(
        employeeId,
        startDate,
        endDate
      )
      setCurrentBalances(response.currentOSPBalances ?? [])
      setEmployeeTransactions(response.employeeTransactions ?? [])
    } catch {
      dispatch(showErrorMessage('Failed to load OSP ledger data'))
      setCurrentBalances([])
      setEmployeeTransactions([])
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, employeeId, startDate, endDate])

  useEffect(() => {
    if (!enabled) return
    fetchLedger()
  }, [enabled, fetchLedger])

  const { simulatedBreakdownRows, simulatedOverviewRows, totalUnpaidBeforeDP, unpaidPerDayBeforeDP } = useMemo(() => {
    const currentCSP100 = findBalanceHours(currentBalances, CSP100_CODE)
    const currentCSP50 = findBalanceHours(currentBalances, CSP50_CODE)

    if (mode === AbsenceLedgerMode.Historical) {
      const days = (extendAbsenceData?.absenceDays ?? [])
        .filter(day => isValidPlannerDate(new Date(day.date)))
        .slice()
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

      const totalHundredUsed = days.reduce((sum, day) => sum + (day.ospUsed ?? 0), 0)
      const totalFiftyUsed = days.reduce((sum, day) => sum + (day.ospUsed50 ?? 0), 0)
      const totalUsedHours = days.reduce((sum, day) => sum + (day.hoursLost ?? 0), 0)
      const totalDP = days.reduce((sum, day) => sum + (day.payApproval ?? 0), 0)
      const totalUnpaidHours = days.reduce((sum, day) => sum + (day.unpaid ?? 0), 0)

      const periodStart = isValidPlannerDate(startDate) ? new Date(startDate) : null
      const periodEnd = isValidPlannerDate(endDate) ? new Date(endDate) : null
      periodStart?.setHours(0, 0, 0, 0)
      periodEnd?.setHours(23, 59, 59, 999)

      const inWindowRecredits = employeeTransactions
        .filter(transaction => transaction.amount > 0)
        .filter(transaction => {
          const txDate = new Date(transaction.transactionDate)
          if (periodStart && txDate < periodStart) return false
          if (periodEnd && txDate > periodEnd) return false
          return true
        })

      const today = new Date()
      const isFutureAbsence = isValidPlannerDate(startDate) && startDate.getTime() > today.getTime()

      let csp100 = isFutureAbsence
        ? projectForwardToPeriodStart(currentCSP100, employeeTransactions, CSP100_CODE, today, startDate)
        : rewindToPeriodStart(currentCSP100, employeeTransactions, CSP100_CODE, startDate, today)
      let csp50 = isFutureAbsence
        ? projectForwardToPeriodStart(currentCSP50, employeeTransactions, CSP50_CODE, today, startDate)
        : rewindToPeriodStart(currentCSP50, employeeTransactions, CSP50_CODE, startDate, today)

      const events: ChronoEvent[] = days.map(day =>
        buildAbsenceEvent(new Date(day.date), day.hoursLost ?? 0, { isOriginal: true })
      )

      inWindowRecredits.forEach(transaction => {
        const isHundred = transaction.balanceXRefCode === CSP100_CODE
        const isFifty = transaction.balanceXRefCode === CSP50_CODE
        if (!isHundred && !isFifty) return
        events.push(
          buildRecreditEvent(
            new Date(transaction.transactionDate),
            isHundred ? transaction.amount : 0,
            isFifty ? transaction.amount : 0
          )
        )
      })

      const startOfDay = (date: Date): number => {
        const copy = new Date(date)
        copy.setHours(0, 0, 0, 0)
        return copy.getTime()
      }

      const dayByDate = new Map(
        days.map(day => [startOfDay(new Date(day.date)), day])
      )

    
      if (isValidPlannerDate(startDate) && isValidPlannerDate(endDate)) {
        eachDayOfInterval({ start: startDate, end: endDate }).forEach(date => {
          if (!dayByDate.has(startOfDay(date))) {
            events.push(buildAbsenceEvent(date, 0, { isUnchecked: true }))
          }
        })
      }

      const sortedEvents = events
        .filter(event => isValidPlannerDate(event.date))
        .sort((a, b) => {
          const dateDiff = startOfDay(a.date) - startOfDay(b.date)
          if (dateDiff !== 0) return dateDiff
          if (a.kind === 'recredit' && b.kind !== 'recredit') return -1
          if (b.kind === 'recredit' && a.kind !== 'recredit') return 1
          return 0
        })

      const historicalRows: LedgerBreakdownRow[] = sortedEvents.map((event, index) => {
        if (event.kind === 'recredit') {
          csp100 += event.hundredCredit
          csp50 += event.fiftyCredit
          return {
            id: `recredit-${index}`,
            date: format(event.date, DATE_DISPLAY_FORMAT),
            usedHours: 0,
            hundredPercentUsed: event.hundredCredit,
            fiftyPercentUsed: event.fiftyCredit,
            balance100: csp100,
            balance50: csp50,
            discretionaryPay: '0.00',
            unpaid: 0,
            isAdjustmentRow: true,
            isNonWorkingDay: false,
          }
        }

        const day = dayByDate.get(startOfDay(event.date))
        const ospUsed = day?.ospUsed ?? 0
        const ospUsed50 = day?.ospUsed50 ?? 0
        const payApproval = day?.payApproval ?? 0
        const unpaid = day?.unpaid ?? 0
        const hoursLost = day?.hoursLost ?? 0

        csp100 -= ospUsed
        csp50 -= ospUsed50

        return {
          id: `absence-${day?.id ?? index}`,
          date: format(event.date, DATE_DISPLAY_FORMAT),
          usedHours: -hoursLost,
          hundredPercentUsed: -ospUsed,
          fiftyPercentUsed: -ospUsed50,
          balance100: csp100,
          balance50: csp50,
          discretionaryPay: formatPendingDP(payApproval),
          unpaid,
          isAdjustmentRow: false,
          isNonWorkingDay: hoursLost === 0,
        }
      })

      const historicalOverview: LedgerOverviewRow[] = [
        {
          id: 'overview-1',
          absencePeriod: buildAbsencePeriodLabel(startDate, endDate),
          hours: totalUsedHours,
          hundredPercentUsed: totalHundredUsed,
          fiftyPercentUsed: totalFiftyUsed,
          discretionaryPay: formatPendingDP(totalDP),
          unpaid: totalUnpaidHours,
        },
      ]

      return {
        simulatedBreakdownRows: historicalRows,
        simulatedOverviewRows: historicalOverview,
        totalUnpaidBeforeDP: totalUnpaidHours + totalDP,
        unpaidPerDayBeforeDP: {},
      }
    }

    const today = new Date()
    const isFutureAbsence = isValidPlannerDate(startDate) && startDate.getTime() > today.getTime()

    const startingCSP100 = isFutureAbsence
      ? projectForwardToPeriodStart(currentCSP100, employeeTransactions, CSP100_CODE, today, startDate)
      : rewindToPeriodStart(currentCSP100, employeeTransactions, CSP100_CODE, startDate, today)
    const startingCSP50 = isFutureAbsence
      ? projectForwardToPeriodStart(currentCSP50, employeeTransactions, CSP50_CODE, today, startDate)
      : rewindToPeriodStart(currentCSP50, employeeTransactions, CSP50_CODE, startDate, today)

    // Step 2 — Build a master event list: original absence days + selected days + re-credits, sorted by date
    const events: ChronoEvent[] = []

    if (extendAbsenceData?.absenceDays) {
      extendAbsenceData.absenceDays.forEach(day => {
        events.push(buildAbsenceEvent(new Date(day.date), day.hoursLost ?? 0, { isOriginal: true }))
      })
    }

    if (selectedDays) {
      selectedDays.forEach(day => {
        const date = typeof day.day === 'string' ? new Date(day.day) : day.day
        events.push(buildAbsenceEvent(date, day.hours, { isUnchecked: !day.checked }))
      })
    }

    // Gap-fill the original absence period with non-working day events for dates
    // the API did not return records for (e.g. weekends). This mirrors the
    // gap-filling that already exists in AbsenceLedgerMode.Historical.
    if (extendAbsenceData?.endDate) {
      const originalEnd = parseISO(extendAbsenceData.endDate)
      if (isValidPlannerDate(startDate) && isValidPlannerDate(originalEnd)) {
        const coveredDates = new Set(
          events
            .filter(e => isValidPlannerDate(e.date))
            .map(e => {
              const d = new Date(e.date)
              d.setHours(0, 0, 0, 0)
              return d.getTime()
            })
        )
        eachDayOfInterval({ start: startDate, end: originalEnd }).forEach(date => {
          const d = new Date(date)
          d.setHours(0, 0, 0, 0)
          if (!coveredDates.has(d.getTime())) {
            events.push(buildAbsenceEvent(date, 0, { isUnchecked: true }))
          }
        })
      }
    }

const periodStart = isValidPlannerDate(startDate) ? new Date(startDate) : null
const periodEnd = isValidPlannerDate(endDate) ? new Date(endDate) : null
periodStart?.setHours(0, 0, 0, 0)
periodEnd?.setHours(23, 59, 59, 999)

employeeTransactions
  .filter(transaction => transaction.amount > 0)
  .filter(transaction => {
    const txDate = new Date(transaction.transactionDate)
    if (periodStart && txDate < periodStart) return false
    if (periodEnd && txDate > periodEnd) return false
    return true
  })
  .forEach(transaction => {
    const isHundred = transaction.balanceXRefCode === CSP100_CODE
    const isFifty = transaction.balanceXRefCode === CSP50_CODE
    if (!isHundred && !isFifty) return
    events.push(
      buildRecreditEvent(
        new Date(transaction.transactionDate),
        isHundred ? transaction.amount : 0,
        isFifty ? transaction.amount : 0
      )
    )
  })

    const startOfDay = (date: Date): number => {
      const copy = new Date(date)
      copy.setHours(0, 0, 0, 0)
      return copy.getTime()
    }

    const sortedEvents = events
      .filter(event => isValidPlannerDate(event.date))
      .sort((a, b) => {
        const dateDiff = startOfDay(a.date) - startOfDay(b.date)
        if (dateDiff !== 0) return dateDiff
        if (a.kind === 'recredit' && b.kind !== 'recredit') return -1
        if (b.kind === 'recredit' && a.kind !== 'recredit') return 1
        return 0
      })

    // Step 3 — Walk through events, cascading absence hours through CSP100 → CSP50 → unpaid.
    // Same-day re-credits sort before absences (see sortedEvents), so the credit lands
    // on the balance first; the recredit row is then pushed immediately, displaying
    // ABOVE the absence row it benefits.
    let csp100 = startingCSP100
    let csp50 = startingCSP50
    const newRows: LedgerBreakdownRow[] = []
    const nextUnpaidPerDay: Record<string, number> = {}

    sortedEvents.forEach((event, index) => {
      const dateLabel = format(event.date, DATE_DISPLAY_FORMAT)

      if (event.kind === 'recredit') {
        csp100 += event.hundredCredit
        csp50 += event.fiftyCredit
        newRows.push({
          id: `recredit-${index}`,
          date: dateLabel,
          usedHours: 0,
          hundredPercentUsed: event.hundredCredit,
          fiftyPercentUsed: event.fiftyCredit,
          balance100: csp100,
          balance50: csp50,
          discretionaryPay: '0.00',
          unpaid: 0,
          isAdjustmentRow: true,
          isNonWorkingDay: false,
        })
        return
      }

      if (event.isUnchecked || event.hours <= 0) {
        newRows.push({
          id: `absence-${index}`,
          date: dateLabel,
          usedHours: 0,
          hundredPercentUsed: 0,
          fiftyPercentUsed: 0,
          balance100: csp100,
          balance50: csp50,
          discretionaryPay: '0.00',
          unpaid: 0,
          isAdjustmentRow: false,
          isNonWorkingDay: true,
        })
        return
      }

      const hundredUsed = Math.min(csp100, event.hours)
      csp100 -= hundredUsed
      const fiftyUsed = Math.min(csp50, event.hours - hundredUsed)
      csp50 -= fiftyUsed
      const unpaidForRow = event.hours - hundredUsed - fiftyUsed

      const dateKey = format(event.date, 'yyyy-MM-dd')
      nextUnpaidPerDay[dateKey] = (nextUnpaidPerDay[dateKey] ?? 0) + unpaidForRow

      newRows.push({
        id: `absence-${index}`,
        date: dateLabel,
        usedHours: -event.hours,
        hundredPercentUsed: -hundredUsed,
        fiftyPercentUsed: -fiftyUsed,
        balance100: csp100,
        balance50: csp50,
        discretionaryPay: '0.00',
        unpaid: unpaidForRow,
        isAdjustmentRow: false,
        isNonWorkingDay: false,
      })
    })

    // Step 4 — Snapshot the total unpaid before DP (used as the cap on the manager's DP input)
    const nextTotalUnpaidBeforeDP = newRows.reduce(
      (sum, row) => (row.isAdjustmentRow ? sum : sum + row.unpaid),
      0
    )

    // Step 5 — Distribute Discretionary Pay chronologically across rows that still have unpaid hours
    let remainingDP = discretionaryPayHours ?? 0
    let totalDP = 0
    let totalUnpaid = 0
    let totalUsedHours = 0
    let totalHundredUsed = 0
    let totalFiftyUsed = 0

    newRows.forEach(row => {
      if (row.isAdjustmentRow) return

      if (row.unpaid > 0) {
        const dpForRow = Math.min(row.unpaid, remainingDP)
        remainingDP -= dpForRow
        row.discretionaryPay = formatPendingDP(dpForRow)
        row.unpaid -= dpForRow
        totalDP += dpForRow
      }

      totalUnpaid += row.unpaid
      totalUsedHours += Math.abs(row.usedHours)
      totalHundredUsed += Math.abs(row.hundredPercentUsed)
      totalFiftyUsed += Math.abs(row.fiftyPercentUsed)
    })

    // Step 6 — Build the overview row from the totals
    const newOverview: LedgerOverviewRow[] = [
      {
        id: 'overview-1',
        absencePeriod: buildAbsencePeriodLabel(startDate, endDate),
        hours: totalUsedHours,
        hundredPercentUsed: totalHundredUsed,
        fiftyPercentUsed: totalFiftyUsed,
        discretionaryPay: formatPendingDP(totalDP),
        unpaid: totalUnpaid,
      },
    ]

    return {
      simulatedBreakdownRows: newRows,
      simulatedOverviewRows: newOverview,
      totalUnpaidBeforeDP: nextTotalUnpaidBeforeDP,
      unpaidPerDayBeforeDP: nextUnpaidPerDay,
    }
  }, [
    currentBalances,
    employeeTransactions,
    extendAbsenceData,
    selectedDays,
    discretionaryPayHours,
    startDate,
    endDate,
    mode,
  ])

  return {
    overviewRows: simulatedOverviewRows,
    breakdownRows: simulatedBreakdownRows,
    totalUnpaidBeforeDP,
    unpaidPerDayBeforeDP,
    isLoading,
  }
}
